import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NOVA_URL } from "@/data/companies";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/companies", label: "Our Companies" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
        scrolled
          ? "bg-white/70 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_1px_20px_rgb(0,0,0,0.03)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-baseline gap-1.5 group"
          >
            <span className="font-heading text-lg md:text-xl font-extrabold tracking-tight text-slate-900">
              AMS
            </span>
            <span className="font-heading text-lg md:text-xl font-light tracking-tight text-slate-500 group-hover:text-blue-600 transition-colors duration-300">
              Venture Holdings
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-medium rounded-full transition-colors duration-200 ${
                    isActive
                      ? "text-slate-900"
                      : "text-slate-500 hover:text-slate-900"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={NOVA_URL}
              data-testid="nav-nova-securenet"
              className="px-4 py-2 text-sm font-medium rounded-full text-slate-500 hover:text-slate-900 transition-colors duration-200"
            >
              Nova SecureNet
            </a>
          </nav>

          <div className="hidden md:block">
            <Button
              asChild
              data-testid="header-contact-cta"
              className="rounded-full bg-slate-900 hover:bg-blue-600 text-white px-5 transition-colors duration-300"
            >
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 text-slate-900"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-white/95 backdrop-blur-xl border-b border-slate-200 px-6 py-4"
        >
          <div className="flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-3 text-base font-medium rounded-lg transition-colors ${
                    isActive ? "text-blue-600 bg-slate-50" : "text-slate-700"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={NOVA_URL}
              data-testid="mobile-nav-nova-securenet"
              className="px-3 py-3 text-base font-medium rounded-lg text-slate-700 transition-colors"
            >
              Nova SecureNet
            </a>
            <Button
              asChild
              className="mt-3 rounded-full bg-slate-900 hover:bg-blue-600 text-white"
            >
              <Link to="/contact" data-testid="mobile-contact-cta">
                Contact Us
              </Link>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};
